import { InventoryItem, Venue, VenueEvent } from 'types/model'

import { InventoryList } from '@modules/inventory/controllers/inventory-list'
import { EventShowBill } from '@modules/event/views/event-show-bill'
import { useVisibilityObserver } from '@hooks/intersection-observer'
import { isVenueEventPassedByInstant, utcToISO } from '@utils/date-time'
import {
  VenueLayout,
  VenueLayoutSection,
  VenueLayoutTable,
} from 'types/model/venue-layout'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { logger } from '@lib/logger'
import { EventHeader } from './event-header'
import { TablelistAttributionLogo } from '@icons/tablelist-attribution'
import { Link } from '@elements/link'

const EVENT_PASSED_POLL_MS = 60_000

export function EventDetailsView({
  venue,
  event,
  tickets,
  packages,
  layouts,
  sections,
  tables,
  embeddedWidget,
  inventoryCalendarMinDate,
  inventoryCalendarMaxDate,
  eventPassedSsr,
}: {
  venue: Venue
  event: VenueEvent
  tickets: InventoryItem[]
  packages: InventoryItem[]
  layouts: VenueLayout[]
  sections: VenueLayoutSection[]
  tables: VenueLayoutTable[]
  embeddedWidget?: boolean
  inventoryCalendarMinDate?: number
  inventoryCalendarMaxDate?: number
  eventPassedSsr?: boolean
}) {
  const showBillObserver = useVisibilityObserver({
    initiallyVisible: true,
    threshold: 0.1,
  })
  const eventTimezone = event.timezone || venue.location.timezone

  const [eventPassed, setEventPassed] = useState(() =>
    typeof eventPassedSsr === 'boolean'
      ? eventPassedSsr
      : isVenueEventPassedByInstant(
          Date.now(),
          event.dateStart,
          event.dateEnd,
          { timezone: eventTimezone }
        )
  )

  useEffect(() => {
    logger.log('EventDetailsView event times', {
      eventId: event.id,
      eventName: event.name,
      date: event.date,
      dateStart: event.dateStart,
      dateEnd: event.dateEnd,
      timezone: eventTimezone,
      dateAsIso: utcToISO(event.date, { timezone: eventTimezone }),
      dateStartAsIso: utcToISO(event.dateStart, { timezone: eventTimezone }),
      dateEndAsIso: utcToISO(event.dateEnd, { timezone: eventTimezone }),
    })
  }, [
    event.date,
    event.dateEnd,
    event.dateStart,
    event.id,
    event.name,
    eventTimezone,
  ])

  useEffect(() => {
    const tick = () => {
      setEventPassed(
        isVenueEventPassedByInstant(
          Date.now(),
          event.dateStart,
          event.dateEnd,
          { timezone: eventTimezone }
        )
      )
    }
    tick()
    const id = window.setInterval(tick, EVENT_PASSED_POLL_MS)
    return () => window.clearInterval(id)
  }, [event.dateEnd, event.dateStart, eventTimezone])

  return (
    <div className="flex flex-col items-start justify-center mx-auto md:flex-row max-w-7xl md:h-fit">
      <div
        ref={showBillObserver.observe}
        className={classNames(
          'flex-1 w-full md:w-1/2 md:sticky md:top-[0px] overflow-hidden',
          {
            'h-[75dvh] md:h-[100dvh]': embeddedWidget,
            'h-[70dvh] md:h-[calc(100dvh-40px)]': !embeddedWidget,
          }
        )}
      >
        <EventShowBill
          posterSrc={event.primaryImage?.filename}
          name={event.name}
        />
      </div>
      <div className="flex-1 w-full mx-auto sm:px-3 pb-3 sm:pb-6 md:h-auto @container">
        {eventPassed ? (
          <div className="p-3 space-y-6">
            <EventHeader
              venueName={venue.name}
              description={event.description}
              venueAddress={venue.location.address}
              eventDate={event.dateStart}
              eventTimezone={eventTimezone}
              eventName={event.name}
            />

            <div className="py-12">
              <p className="border rounded p-3 border-foreground/70 dark:border-dark-foreground/70 !text-md sm:!text-md md:!text-lg text-center">
                Sorry, this event has already passed. There is no inventory
                available
              </p>
            </div>
          </div>
        ) : (
          <InventoryList
            venue={venue}
            event={event}
            tickets={tickets}
            packages={packages}
            layouts={layouts}
            sections={sections}
            tables={tables}
            showDatePicker={false}
            initialDate={utcToISO(event.date)}
            inventoryCalendarMinDate={inventoryCalendarMinDate}
            inventoryCalendarMaxDate={inventoryCalendarMaxDate}
            showEvent
            showHeader
            stickyMap={false}
            showEventPoster={!showBillObserver.visible}
          />
        )}
        {embeddedWidget && (
          <div className="pb-4 mt-8">
            <Link
              href="https://www.tablelistpro.com/"
              target="_blank"
              rel="noreferrer"
              className="!text-foreground dark:!text-dark-foreground"
            >
              <TablelistAttributionLogo />
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
